import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input } from "@nextui-org/react";
import { useState } from "react";
import { Calendar } from "lucide-react";

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  { value: 'America/Anchorage', label: 'Anchorage (AKST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Halifax', label: 'Halifax (AST/ADT)' },
  { value: 'America/St_Johns', label: 'St. Johns (NST/NDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' }
];

interface SchedulePostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSchedule: (date: Date, timezone: string) => void;
}

export function SchedulePostModal({
  open,
  onOpenChange,
  onSchedule,
}: SchedulePostModalProps) {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const handleConfirm = () => {
    try {
      const dateString = `${selectedDate}T${selectedTime}`;
      const tzDate = new Date(dateString);

      if (isNaN(tzDate.getTime())) {
        throw new Error('Invalid date');
      }

      onSchedule(tzDate, timezone);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating scheduled date:', error);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        <ModalHeader className="text-xl">Schedule Post</ModalHeader>
        <ModalBody className="pb-6">
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <Input
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e?.target?.value)}
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Time</label>
              <Input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e?.target?.value)}
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Timezone</label>
              <select
                className="w-full rounded-md border border-default-200 bg-transparent px-3 py-2"
                value={timezone}
                onChange={(e) => setTimezone(e?.target?.value)}
              >
                {TIMEZONES?.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="light"
              onPress={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleConfirm}
            >
              Schedule
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}